import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, StatusBadge } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { AuditPanel } from "@/components/app/AuditPanel";
import { useMe } from "@/components/app/MeContext";
import { ProspectConvert } from "@/components/sales/ProspectConvert";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const EMPTY = { company_name: "", contact_name: "", contact_email: "", contact_phone: "", city: "", state: "", industry: "", source: "", status: "new", sales_owner: "", notes: "" };
const FIELDS = [["company_name", "Company name *"], ["contact_name", "Contact name"], ["contact_email", "Contact email"], ["contact_phone", "Contact phone"],
  ["city", "City"], ["state", "State"], ["industry", "Industry"], ["source", "Source (how we met)"]];
const COLS = [
  { key: "code", label: "Code", mono: true },
  { key: "company_name", label: "Company", wrap: true },
  { key: "contact_name", label: "Contact" },
  { key: "city", label: "City", value: (p) => [p.city, p.state].filter(Boolean).join(", ") },
  { key: "sales_owner", label: "Sales owner" },
  { key: "status", label: "Status", render: (p) => <StatusBadge status={p.status} /> },
  { key: "converted_customer_name", label: "Customer", wrap: true },
  { key: "active", label: "Active", render: (p) => (p.active ? "Yes" : <span className="text-slate-400">No</span>) },
];

function OwnerSelect({ value, onChange }) {
  const { data } = useFetch("/sales-people");
  const names = (data || []).filter((p) => p.active).map((p) => p.name);
  const opts = value && !names.includes(value) ? [value, ...names] : names;
  return (
    <div><Label>Assigned sales owner</Label>
      <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)}>
        <SelectTrigger data-testid="prospect-sales_owner"><SelectValue placeholder="Pick from Sales Team" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">Not assigned</SelectItem>
          {opts.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
        </SelectContent>
      </Select></div>
  );
}

function ProspectDialog({ prospect, open, onOpenChange, onSaved }) {
  const { can } = useMe();
  const [f, setF] = useState(prospect ? { ...EMPTY, ...prospect } : EMPTY);
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] || ""]));
      prospect ? await api.put(`/prospects/${prospect.id}`, body) : await api.post("/prospects", body);
      toast.success("Prospect saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (v) => { try { await api.post(`/prospects/${prospect.id}/status`, { active: v }); onSaved(); onOpenChange(false); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-dialog">
        <DialogHeader><DialogTitle>{prospect ? `${prospect.code} · ${prospect.company_name}` : "New prospect"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => <div key={k}><Label>{l}</Label><Input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`prospect-${k}`} /></div>)}
          <OwnerSelect value={f.sales_owner} onChange={(v) => setF({ ...f, sales_owner: v })} />
          <div><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })} disabled={f.status === "converted"}>
              <SelectTrigger data-testid="prospect-status"><SelectValue /></SelectTrigger>
              <SelectContent>{[...STATUSES, ...(f.status === "converted" ? ["converted"] : [])].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="prospect-notes" /></div>
        </div>
        {prospect && <ProspectConvert prospect={prospect} onDone={() => { onSaved(); onOpenChange(false); }} />}
        {prospect && <AuditPanel entityId={prospect.id} />}
        <DialogFooter className="gap-2">
          {prospect && can("sales.delete") && <label className="flex items-center gap-2 text-xs mr-auto"><Switch checked={prospect.active} onCheckedChange={toggle} data-testid="prospect-active-switch" />Active</label>}
          <Button onClick={save} disabled={!f.company_name.trim()} data-testid="prospect-save-btn">Save prospect</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SalesProspects() {
  const { can } = useMe();
  const { data, reload } = useFetch("/prospects");
  const [edit, setEdit] = useState(null);
  return (
    <div data-testid="prospects-page">
      <PageHeader title="Prospects" subtitle="Parties not yet in the Customers Master. Quote them now; convert them to a customer before an LM is booked.">
        {can("sales.create") && <Button onClick={() => setEdit({})} data-testid="prospect-new-btn"><Plus className="h-4 w-4 mr-1" />New prospect</Button>}
      </PageHeader>
      <DataTable testid="prospects-table" columns={COLS} rows={data} onRowClick={(p) => setEdit(p)} exportName="prospects" exportPerm="sales.export" empty="No prospects yet" />
      {edit && <ProspectDialog key={edit.id || "new"} prospect={edit.id ? edit : null} open onOpenChange={(v) => !v && setEdit(null)} onSaved={reload} />}
    </div>
  );
}
